import React, { useState } from 'react';
import './Contact.css';
import bannerImg from '../images/banner.jpg';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const SUBJECT_OPTIONS = [
  'General Inquiry',
  'Admissions',
  'School Fees',
  'Academic Programs',
  'School Tours',
  'Other',
];

/* Orozo, Abuja coordinates — real OpenStreetMap embed */
const MAP_SRC =
  'https://www.openstreetmap.org/export/embed.html?bbox=7.2150%2C9.0450%2C7.2450%2C9.0650&layer=mapnik&marker=9.0550%2C7.2300';

function FieldLabel({ htmlFor, children, required }) {
  return (
    <label htmlFor={htmlFor} className="contact-field-label">
      {children}{required && <span aria-hidden="true"> *</span>}
    </label>
  );
}

function Contact() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: SUBJECT_OPTIONS[0],
    message: '',
  });
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');
    try {
      const res = await fetch(`${API_BASE}/submissions/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${form.firstName} ${form.lastName}`.trim(),
          email: form.email,
          subject: form.subject,
          message: form.message,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed.');
      setStatus('success');
      setForm({ firstName: '', lastName: '', email: '', subject: SUBJECT_OPTIONS[0], message: '' });
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  return (
    <div className="contact-page">

      {/* ── HERO ──────────────────────────────────── */}
      <section className="contact-hero" aria-label="Contact Twinkle Toes Schools">
        <div
          className="contact-hero-photo"
          style={{ backgroundImage: `url(${bannerImg})` }}
          aria-hidden="true"
        />
        <div className="contact-hero-veil" aria-hidden="true" />
        <div className="contact-hero-content">
          <p className="contact-hero-kicker">We're Here to Help</p>
          <h1>Get in Touch</h1>
          <p>
            Whether you're curious about admissions, our programmes, or simply want
            to schedule a visit — we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* ── BODY GRID ─────────────────────────────── */}
      <div className="contact-body">

        {/* LEFT: Info + Map */}
        <div className="contact-left">
          <div className="contact-info-block">
            <h2 className="contact-info-heading">Contact &amp; Location</h2>
            <p className="contact-info-intro">
              Our admissions team is available Monday to Friday. Pop in for a tour or
              reach us through the channels below.
            </p>

            <ul className="contact-details-list">
              <li>
                <span className="contact-detail-icon" aria-hidden="true">
                  {/* Location pin — custom SVG, not generic */}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="var(--secondary-container)" opacity=".18"/>
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="var(--primary)" strokeWidth="1.6" strokeLinejoin="round"/>
                    <circle cx="12" cy="9" r="2.5" fill="var(--primary)"/>
                  </svg>
                </span>
                <div>
                  <span className="contact-detail-label">Campus Address</span>
                  <span className="contact-detail-value">Cajaah Estate, Orozo, Abuja, Nigeria</span>
                </div>
              </li>
              <li>
                <span className="contact-detail-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="5" y="2" width="14" height="20" rx="3" fill="var(--secondary-container)" opacity=".18"/>
                    <rect x="5" y="2" width="14" height="20" rx="3" stroke="var(--primary)" strokeWidth="1.6"/>
                    <path d="M9 18h6" stroke="var(--primary)" strokeWidth="1.6" strokeLinecap="round"/>
                    <circle cx="12" cy="7" r="1" fill="var(--primary)"/>
                  </svg>
                </span>
                <div>
                  <span className="contact-detail-label">Phone</span>
                  <span className="contact-detail-value">
                    <a href="tel:+2348012345678">+234 801 234 5678</a>
                    <a href="tel:+2349012345678">+234 901 234 5678</a>
                  </span>
                </div>
              </li>
              <li>
                <span className="contact-detail-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="2" y="4" width="20" height="16" rx="3" fill="var(--secondary-container)" opacity=".18"/>
                    <rect x="2" y="4" width="20" height="16" rx="3" stroke="var(--primary)" strokeWidth="1.6"/>
                    <path d="M2 8l10 6 10-6" stroke="var(--primary)" strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                </span>
                <div>
                  <span className="contact-detail-label">Email</span>
                  <span className="contact-detail-value">
                    <a href="mailto:info@twinkletoesschools.com">info@twinkletoesschools.com</a>
                    <a href="mailto:admissions@twinkletoesschools.com">admissions@twinkletoesschools.com</a>
                  </span>
                </div>
              </li>
              <li>
                <span className="contact-detail-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" fill="var(--secondary-container)" opacity=".18"/>
                    <circle cx="12" cy="12" r="9" stroke="var(--primary)" strokeWidth="1.6"/>
                    <path d="M12 7v5l3 3" stroke="var(--primary)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <div>
                  <span className="contact-detail-label">Office Hours</span>
                  <span className="contact-detail-value">Monday – Friday, 8:00 AM – 5:00 PM</span>
                </div>
              </li>
            </ul>
          </div>

          {/* Live OpenStreetMap embed */}
          <div className="contact-map-wrap">
            <iframe
              className="contact-map-iframe"
              src={MAP_SRC}
              title="Twinkle Toes Schools — Orozo, Abuja location on map"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
            <a
              className="contact-map-link"
              href="https://www.openstreetmap.org/?mlat=9.0550&mlon=7.2300#map=14/9.0550/7.2300"
              target="_blank"
              rel="noreferrer"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              Open in OpenStreetMap
            </a>
          </div>
        </div>

        {/* RIGHT: Form */}
        <div className="contact-form-card">
          <div className="contact-form-header">
            <h2>Send a Message</h2>
            <p>We respond within 1–2 business days.</p>
          </div>

          {status === 'success' ? (
            <div className="contact-success-state">
              <div className="contact-success-icon" aria-hidden="true">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="12" r="10" stroke="var(--primary)" opacity=".25"/>
                  <path d="M8 12l3 3 5-5" stroke="var(--primary)" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Message received!</h3>
              <p>A member of our team will be in touch with you shortly.</p>
              <button
                className="contact-submit-btn"
                onClick={() => setStatus(null)}
              >
                Send another message
              </button>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              {status === 'error' && (
                <div className="contact-error-banner" role="alert">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {error}
                </div>
              )}

              <div className="contact-form-row">
                <div className="contact-field">
                  <FieldLabel htmlFor="cf-first" required>First Name</FieldLabel>
                  <input
                    id="cf-first" name="firstName" type="text"
                    className="contact-input" value={form.firstName}
                    onChange={handleChange} required
                    placeholder="Jane" autoComplete="given-name"
                  />
                </div>
                <div className="contact-field">
                  <FieldLabel htmlFor="cf-last">Last Name</FieldLabel>
                  <input
                    id="cf-last" name="lastName" type="text"
                    className="contact-input" value={form.lastName}
                    onChange={handleChange}
                    placeholder="Doe" autoComplete="family-name"
                  />
                </div>
              </div>

              <div className="contact-field">
                <FieldLabel htmlFor="cf-email" required>Email Address</FieldLabel>
                <input
                  id="cf-email" name="email" type="email"
                  className="contact-input" value={form.email}
                  onChange={handleChange} required
                  placeholder="jane@example.com" autoComplete="email"
                />
              </div>

              <div className="contact-field">
                <FieldLabel htmlFor="cf-subject">Subject</FieldLabel>
                <select
                  id="cf-subject" name="subject"
                  className="contact-select" value={form.subject}
                  onChange={handleChange}
                >
                  {SUBJECT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="contact-field">
                <FieldLabel htmlFor="cf-message" required>Message</FieldLabel>
                <textarea
                  id="cf-message" name="message"
                  className="contact-textarea" value={form.message}
                  onChange={handleChange} required rows={5}
                  placeholder="How can we help you today?"
                />
              </div>

              <button
                type="submit"
                className="contact-submit-btn"
                disabled={status === 'loading'}
                aria-busy={status === 'loading'}
              >
                {status === 'loading' ? (
                  <span className="contact-btn-spinner" aria-hidden="true" />
                ) : (
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                )}
                {status === 'loading' ? 'Sending…' : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default Contact;
